import { useState } from 'react';

import { useGlobalSnackbar } from '@/contexts/snackContext';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useLocalization } from '@/hooks/useLocalization';
import ClientForm from '@/modules/clientServices/ClientForm';
import {
  buildEditInvestorPayload,
  ClientFormValues,
} from '@/modules/clientServices/clientForm.helpers';
import {
  useGetAdvisorInvestorSetupQuery,
  useUpdateAdvisorInvestorSetupMutation,
} from '@/store/usersApi';
import Close from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

type EditInvestorFormProps = {
  userId: number;
  open: boolean;
  onClose: () => void;
};

const EditInvestorForm = ({ userId, open, onClose }: EditInvestorFormProps) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const authLoaded = useAuthIsLoaded();
  const { showSnackbar } = useGlobalSnackbar();

  /** DOM id linking an external submit control to the ClientForm's <form>. */
  const EDIT_CLIENT_MODAL_FORM = 'edit-client-modal-form';

  // Data to load
  const { data, isLoading } = useGetAdvisorInvestorSetupQuery(
    authLoaded ? userId : skipToken,
  );
  const [updateInvestor] = useUpdateAdvisorInvestorSetupMutation();

  const [isValid, setIsValid] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasError, setError] = useState(false);

  // Submission is owned here; ClientForm only emits the validated values.
  const handleSubmit = async (values: ClientFormValues) => {
    try {
      setIsSaving(true);
      setError(false);
      const payload = buildEditInvestorPayload(values);

      await updateInvestor({ userId, body: payload }).unwrap();
      handleClose();
      showSnackbar(
        t('advisor.editClient.updateSuccess', { name: payload.Name }),
      );
    } catch {
      setError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setIsSaving(false);
    setError(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth='sm'
      onClose={onClose}
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
        >
          <Typography variant='h6'>
            {t('editUserInformation', 'Edit user information')}
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {hasError && (
          <Alert
            severity='error'
            sx={{ mb: 3 }}
          >
            {t('advisor.editClient.updateFailure')}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ClientForm
            submittedData={data}
            isSaving={isSaving}
            onSubmit={handleSubmit}
            onValidityChange={setIsValid}
            formId={EDIT_CLIENT_MODAL_FORM}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel', 'Cancel')}</Button>
        <Button
          variant='contained'
          disabled={!isValid || isSaving}
          type='submit'
          form={EDIT_CLIENT_MODAL_FORM}
        >
          {isSaving ? t('saving', 'Saving...') : t('save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditInvestorForm;
